import { Check, ChevronDown, Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { SmsTemplateSchema } from '@/services/api/generated/smsTemplatesService';

interface TemplateSelectorProps {
  templates: SmsTemplateSchema[];
  selectedTemplateId: string | null;
  recommendedTemplateId: string | null;
  onSelect: (template: SmsTemplateSchema | null) => void;
  disabled?: boolean;
  className?: string;
}

export const TemplateSelector = ({
  templates,
  selectedTemplateId,
  recommendedTemplateId,
  onSelect,
  disabled,
  className,
}: TemplateSelectorProps) => {
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const globalTemplates = templates.filter((t) => t.is_global);
  const firmTemplates = templates.filter((t) => !t.is_global);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedTemplate && 'text-zinc-500',
            className,
          )}
        >
          <span className="truncate">
            {selectedTemplate?.name ?? 'Select a template...'}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {selectedTemplateId === recommendedTemplateId && (
              <span className="flex items-center gap-0.5 text-amber-600 text-xs">
                <Star className="h-3 w-3 fill-current" />
                Recommended
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search templates..." />
          <CommandList>
            <CommandEmpty>No templates found.</CommandEmpty>

            {firmTemplates.length > 0 && (
              <CommandGroup heading="Firm Templates">
                {firmTemplates.map((template) => (
                  <CommandItem
                    key={template.id}
                    value={template.name}
                    onSelect={() => onSelect(template)}
                    className="flex flex-col items-start gap-1 py-2"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Check
                        className={cn(
                          'h-4 w-4 flex-shrink-0',
                          selectedTemplateId === template.id
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className="font-medium truncate flex-1">
                        {template.name}
                      </span>
                      {template.id === recommendedTemplateId && (
                        <span className="flex items-center gap-0.5 text-amber-600 text-xs">
                          <Star className="h-3 w-3 fill-current" />
                        </span>
                      )}
                      {template.is_default && (
                        <span className="text-xs text-zinc-500">Default</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 line-clamp-2 pl-6">
                      {template.content.slice(0, 100)}
                      {template.content.length > 100 ? '...' : ''}
                    </p>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {globalTemplates.length > 0 && (
              <CommandGroup heading="Global Templates">
                {globalTemplates.map((template) => (
                  <CommandItem
                    key={template.id}
                    value={template.name}
                    onSelect={() => onSelect(template)}
                    className="flex flex-col items-start gap-1 py-2"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Check
                        className={cn(
                          'h-4 w-4 flex-shrink-0',
                          selectedTemplateId === template.id
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className="font-medium truncate flex-1">
                        {template.name}
                      </span>
                      {template.id === recommendedTemplateId && (
                        <span className="flex items-center gap-0.5 text-amber-600 text-xs">
                          <Star className="h-3 w-3 fill-current" />
                        </span>
                      )}
                      {template.cadence_day != null && (
                        <span className="text-xs text-zinc-500">
                          Day {template.cadence_day}+
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 line-clamp-2 pl-6">
                      {template.content.slice(0, 100)}
                      {template.content.length > 100 ? '...' : ''}
                    </p>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
